// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import type { StrongOmit } from '@evolvemep/api-ts';
import { ActionIcon, Flex, type FlexProps, TextInput, type TextInputProps } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { isNotNil } from 'helpers/isNotNil';

type Props = {
  defaultValue?: string;
  onClose: () => void;
  buttonSide?: 'left' | 'right';
  loading?: boolean;
  validate?: (value: string) => boolean;
  flexProps?: FlexProps;
} & (
  | {
      type: 'submit';
      onSubmit?: never;
    }
  | {
      onSubmit: (value: string) => void;
      type?: never;
    }
) &
  StrongOmit<TextInputProps, 'value' | 'onChange' | 'onSubmit' | 'type'>;

export const InlineEditor = ({
  buttonSide = 'left',
  defaultValue,
  onClose,
  validate,
  type,
  onSubmit,
  loading,
  flexProps,
  ...props
}: Props) => {
  const [value, setValue] = useState(defaultValue ?? '');

  return (
    <Flex
      align="center"
      gap="xs"
      direction={buttonSide === 'left' ? 'row' : 'row-reverse'}
      onClick={(e) => e.stopPropagation()}
      {...flexProps}
    >
      <Flex gap="xs">
        <AgGridStyleTooltip label="Cancel edit" withArrow>
          <ActionIcon variant="light" size="sm" onClick={onClose} disabled={loading}>
            <EvolveIcon icon="Close" size="sm" />
          </ActionIcon>
        </AgGridStyleTooltip>
        <AgGridStyleTooltip label="Save changes" withArrow>
          <ActionIcon
            loading={loading}
            variant="filled"
            size="sm"
            disabled={isNotNil(validate) && !validate(value)}
            type={type}
            onClick={() => onSubmit?.(value)}
          >
            <EvolveIcon icon="Check" size="sm" />
          </ActionIcon>
        </AgGridStyleTooltip>
      </Flex>
      <TextInput
        autoFocus
        disabled={loading}
        size="xs"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onSubmit?.(value);
          }
        }}
        {...props}
        value={value}
        onChange={(event) => setValue(event.currentTarget.value)}
      />
    </Flex>
  );
};
